import React, { useState, useEffect, useCallback, useContext } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useTranslator } from '@jutro/locale';
// import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { MetadataContent } from '@jutro/legacy/uiconfig';
import { ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import { MLAgentChoiceReportService } from 'gw-capability-gateway';

import MLPartnerCarrierLobsComponent from './MLPartnerCarrierLobsComponent';

import metadata from './MLPartnerCarrierPopup.metadata.json5';
import messages from './MLPartnerCarrierPopup.messages';
import styles from './MLPartnerCarrierPopup.module.scss';

const MLPartnerCarrierPopup = (props) => {
    const {
        jobNumber,
        onSave,
        onCancel,
        authHeader
    } = props;
    const viewModelService = useContext(ViewModelServiceContext);
    const translator = useTranslator();
    // const { authHeader } = useAuthentication();

    const [pendingChoices, updatePendingChoices] = useState(new Map());
    const [initChoices, updateInitChoices] = useState(new Map());
    const [showNoChangeButton, updateShowNoChangeButton] = useState(false);
    const [options, updateOptions] = useState([]);

    const { loadReport, saveReport } = MLAgentChoiceReportService();

    const LOB_DISPLAY_KEYS = [
        {
            displayKey: translator(messages.auto),
            code: 'Auto'
        },
        {
            displayKey: translator(messages.home),
            code: 'Home'
        }, {
            displayKey: translator(messages.other),
            code: 'Other'
        }];
    
    const JOB_STATUS_DISPLAY_KEYS = {
        sub_quoted: {
            title: translator(messages.partnerCarriersQuoted),
            subTitle: translator(messages.partnerCarriersSelect),
            priority: 1
        },
        sub_bound: {
            title: translator(messages.partnerCarriersBound),
            subTitle: translator(messages.partnerCarriersBoundSelect),
            priority: 2
        }
    };

    const choiceOptions = viewModelService.productMetadata.get('pc').types.getTypelist('ChoiceReportOptions_ML').codes;
    const jobStatus = viewModelService.productMetadata.get('pc').types.getTypelist('CustomHistoryType').getFilter('quoted_and_bound').codes;

    useEffect(() => {
        const initPendingChoices = (report) => {
            const selectionKey = `${report.status}_${report.lob}_${report.agentChoice}`;
            const selection = {
                agentChoice: report.agentChoice,
                lob: report.lob,
                status: report.status,
                jobNumber: report.jobNumber
            };
            pendingChoices.set(selectionKey, selection);
            updatePendingChoices(pendingChoices);
        };

        const initSelected = (status, lob, option) => {
            return pendingChoices.has(`${status}_${lob}_${option}`);
        };

        const getOptions = () => {
            return jobStatus.map((status) => {
                return {
                    jobStatus: {name: status.name, code: status.code},
                    statusDisplayKey: JOB_STATUS_DISPLAY_KEYS[status.code],
                    lobs: LOB_DISPLAY_KEYS.map((lob) => {
                        return {
                            displayKey: lob.displayKey,
                            code: lob.code,
                            choices: choiceOptions
                                .filter((option) => {
                                    if (status.code === 'sub_bound' && option.code === 'not_selected') {
                                        return false;
                                    }
                                    return true;
                                })
                                .map((option) => {
                                    return {
                                        name: option.name,
                                        code: option.code,
                                        selected: initSelected(status.code, lob.code, option.code)};
                                }),
                            selected: initSelected(this)
                        };
                    })
                };
            }).sort((a, b) => {
                return a.statusDisplayKey.priority - b.statusDisplayKey.priority;
            });
        };

        const initOptions = () => {
            const loadReportPromise = loadReport(jobNumber, authHeader);
            // ModalService.showProgressDialog('ml.edge.agent.directives.MLPartnerCarrierDirective.MLPartnerCarrierDirectiveCtrl.Loading saved report', loadReportPromise);
            loadReportPromise.then((reports) => {
                if (reports) {
                    reports.forEach((r) => {
                        // sync with selected choices
                        initPendingChoices(r);
                    });
                    updateShowNoChangeButton(true);
                }
                const getAllOption = getOptions();
                updateOptions(getAllOption);
                updateInitChoices(new Map(pendingChoices));
            });
        };

        initOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const generateOverrides = useCallback(() => {
        const optionOverrides = options.map((option, index) => {
            return {
                [`title${index}`]: {
                    content: option.statusDisplayKey.title
                },
                [`subTitle${index}`]: {
                    content: option.statusDisplayKey.subTitle
                },
                [`mlPartnerCarrierLobsComponent${index}`]: {
                    optionIndex: index,
                    option,
                    pendingChoices,
                    jobNumber,
                    updatePendingChoices
                }
            };
        });

        return Object.assign({}, ...optionOverrides);
    }, [jobNumber, options, pendingChoices]);

    const savesReport = () => {
        saveReport(jobNumber, [...pendingChoices.values()], authHeader);
        if (onSave) {
            onSave();
        }
    };

    const disableSaveButton = useCallback(() => {
        if (!pendingChoices || !initChoices) {
            return true;
        }
        return _.isEqual([...pendingChoices.values()], [...initChoices.values()]);
    }, [initChoices, pendingChoices]);

    const overrides = {
        optionsIterable: {
            data: options
        },
        saveButton: {
            disabled: disableSaveButton(),
            onClick: savesReport
        },
        noChangesNeededButton: {
            visible: showNoChangeButton,
            onClick: onCancel
        },
        ...generateOverrides()
    };

    const resolvers = {
        resolveClassNameMap: styles,
        resolveComponentMap: {
            mlPartnerCarrierLobsComponent: MLPartnerCarrierLobsComponent
        }
    };

    return (
        <MetadataContent
            uiProps={metadata.componentContent}
            overrideProps={overrides}
            {...resolvers} />
    );
};

MLPartnerCarrierPopup.propTypes = {
    jobNumber: PropTypes.string.isRequired,
};

export default MLPartnerCarrierPopup;
